import React from "react";
import { useQuery } from "@apollo/client";
import { PROJECT_STATS } from "../../graphql/gqlProject";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Box,
  Chip,
} from "@mui/material";

type ProjectStatsResponse = {
  getProjectStats: {
    total: number;
    byStatus: { PRIVATE: number; SHARED: number; PUBLIC: number };
  };
};

const StatCard = ({
  label,
  value,
  color,
}: {
  label: string;
  value: number | string;
  color?: "default" | "primary" | "success" | "warning" | "info" | "error";
}) => (
  <Card elevation={2} sx={{ borderRadius: 3 }}>
    <CardContent>
      <Typography variant="overline" sx={{ opacity: 0.8 }}>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ mt: 0.5 }}>
        {value}
      </Typography>
      {color && (
        <Box sx={{ mt: 1 }}>
          <Chip size="small" color={color} label={label} />
        </Box>
      )}
    </CardContent>
  </Card>
);

const ProjectStatsCards: React.FC = () => {
  const { data, loading, error } =
    useQuery<ProjectStatsResponse>(PROJECT_STATS);

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );

  if (error)
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">
          {error.message || "Failed to load stats"}
        </Typography>
      </Box>
    );

  const total = data?.getProjectStats.total ?? 0;
  const by = data?.getProjectStats.byStatus ?? {
    PRIVATE: 0,
    SHARED: 0,
    PUBLIC: 0,
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard label="Total Projects" value={total} color="primary" />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard label="Public" value={by.PUBLIC} color="success" />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard label="Shared" value={by.SHARED} color="warning" />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard label="Private" value={by.PRIVATE} color="default" />
      </Grid>
    </Grid>
  );
};

export default ProjectStatsCards;
